import {
  BENEFIT_PERIODS,
  BENEFIT_RULE_KINDS,
  COVER_VALIDITIES,
  COVERAGE_RELATIONSHIPS,
  DEBTOR_AGE_BANDS,
  INSURER_CLAIM_STATES,
  INSURER_GATE_STATES,
  INSURER_PREAUTH_STATES,
  INSURER_READINESS_CODES,
  INSURER_WRITE_OFF_REASONS,
  REMITTANCE_METHODS,
  SERVICE_ITEM_CATEGORIES,
} from '@wodi/domain';
import { z } from 'zod';

import { ACTIVE_STATUSES } from './catalogue.ts';
import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { amountSchema } from './money.ts';

// Private insurance, manual workflow (docs/plans/phase2-private-insurance.md §4). Money
// crosses as cents strings with currency (ADR 0006). Member numbers are typed by people and
// travel only in bodies, never in URLs or query strings.

export {
  BENEFIT_PERIODS,
  BENEFIT_RULE_KINDS,
  COVERAGE_RELATIONSHIPS,
  INSURER_CLAIM_STATES,
  INSURER_PREAUTH_STATES,
  INSURER_WRITE_OFF_REASONS,
  REMITTANCE_METHODS,
};

const memberNumberSchema = z.string().trim().min(1).max(40);

// --- Cover --------------------------------------------------------------------------

export const coverageViewSchema = z.object({
  id: z.uuid(),
  payerId: z.uuid(),
  payerName: z.string(),
  payerKind: z.string(),
  schemeId: z.uuid(),
  schemeName: z.string(),
  source: z.enum(['eligibility', 'recorded']),
  memberNumber: z.string().nullable(),
  relationship: z.enum(COVERAGE_RELATIONSHIPS).nullable(),
  principalMemberNumber: z.string().nullable(),
  principalName: z.string().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  status: z.enum(['active', 'ended', 'entered_in_error']),
  statusReason: z.string().nullable(),
  /** Validity today, East Africa Time. */
  validity: z.enum(COVER_VALIDITIES),
  version: z.number().int(),
});
export type CoverageView = z.infer<typeof coverageViewSchema>;

export const coverageListSchema = z.object({ items: z.array(coverageViewSchema) });

export const recordCoverageRequestSchema = z
  .object({
    schemeId: z.uuid(),
    memberNumber: memberNumberSchema,
    relationship: z.enum(COVERAGE_RELATIONSHIPS),
    principalMemberNumber: memberNumberSchema.optional(),
    principalName: z.string().trim().min(2).max(120).optional(),
    periodStart: isoDaySchema,
    periodEnd: isoDaySchema.optional(),
  })
  .refine((v) => v.relationship !== 'dependant' || v.principalMemberNumber !== undefined, {
    message: "A dependant's cover needs the principal member's number",
    path: ['principalMemberNumber'],
  })
  .refine((v) => v.periodEnd === undefined || v.periodEnd >= v.periodStart, {
    message: 'The cover ends before it starts',
    path: ['periodEnd'],
  });
export type RecordCoverageRequest = z.infer<typeof recordCoverageRequestSchema>;

export const coverageStatusChangeSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: reasonSchema,
});

// --- Benefit rules ------------------------------------------------------------------

export const benefitRuleViewSchema = z.object({
  id: z.uuid(),
  payerId: z.uuid(),
  schemeId: z.uuid(),
  kind: z.enum(BENEFIT_RULE_KINDS),
  period: z.enum(BENEFIT_PERIODS).nullable(),
  category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
  amount: amountSchema,
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
  /** The rule in words: "Limit per benefit year, lab tests: KES 20,000". */
  sentence: z.string(),
});
export type BenefitRuleView = z.infer<typeof benefitRuleViewSchema>;

export const benefitRuleListSchema = z.object({ items: z.array(benefitRuleViewSchema) });

export const createBenefitRuleRequestSchema = z
  .object({
    kind: z.enum(BENEFIT_RULE_KINDS),
    period: z.enum(BENEFIT_PERIODS).nullable(),
    category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
    amount: amountSchema,
  })
  .refine((v) => (v.kind === 'preauth_required') === (v.period === null), {
    message: 'Limits and excess need a period; approval rules have none',
    path: ['period'],
  })
  .refine((v) => v.kind === 'preauth_required' || v.amount.cents !== '0', {
    message: 'A limit or excess must be more than zero',
    path: ['amount'],
  });

export const deactivateBenefitRuleRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: reasonSchema,
});

// --- Pre-authorisations -------------------------------------------------------------

export const insurerPreauthViewSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  payerName: z.string(),
  serviceItemId: z.uuid().nullable(),
  serviceName: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.uuid().nullable(),
  requested: amountSchema.nullable(),
  requestedAt: z.iso.datetime(),
  requestedBy: actorSchema,
  state: z.enum(INSURER_PREAUTH_STATES),
  insurerReference: z.string().nullable(),
  approved: amountSchema.nullable(),
  validUntil: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  statusReason: z.string().nullable(),
  version: z.number().int(),
});
export type InsurerPreauthView = z.infer<typeof insurerPreauthViewSchema>;

export const recordInsurerPreauthRequestSchema = z.object({
  /** Null: the whole visit. */
  serviceItemId: z.uuid().nullable(),
  source: z
    .object({
      type: z.enum(['lab_order', 'imaging_order', 'procedure', 'prescription_line']),
      id: z.uuid(),
    })
    .nullable()
    .default(null),
  requested: amountSchema.nullable().default(null),
  note: z.string().trim().max(500).optional(),
});

export const decideInsurerPreauthRequestSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('approved'),
    expectedVersion: z.number().int().min(1),
    insurerReference: z.string().trim().min(1).max(64),
    approved: amountSchema,
    validUntil: isoDaySchema.optional(),
  }),
  z.object({
    outcome: z.literal('declined'),
    expectedVersion: z.number().int().min(1),
    insurerReference: z.string().trim().min(1).max(64).optional(),
    reason: z.string().trim().min(2).max(500),
  }),
]);

export const cancelInsurerPreauthRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: reasonSchema,
});

// --- The visit's insurance ---------------------------------------------------------

export const visitChargeBenefitSchema = z.object({
  chargeId: z.uuid(),
  itemName: z.string(),
  ruleSentence: z.string(),
  kind: z.enum(['limit', 'excess']),
  shifted: amountSchema,
});

export const visitInsuranceSchema = z.object({
  visitId: z.uuid(),
  /** Null when the visit isn't billed to an insurer. */
  cover: coverageViewSchema.nullable(),
  payerAmount: amountSchema,
  patientAmount: amountSchema,
  benefits: z.array(visitChargeBenefitSchema),
  /** Charges that need the insurer's approval and where each stands. */
  approvals: z.array(
    z.object({
      chargeId: z.uuid(),
      itemName: z.string(),
      serviceItemId: z.uuid(),
      state: z.enum(INSURER_GATE_STATES),
    }),
  ),
  preauths: z.array(insurerPreauthViewSchema),
  claim: z.object({ id: z.uuid(), claimNumber: z.string(), sentence: z.string() }).nullable(),
});
export type VisitInsuranceView = z.infer<typeof visitInsuranceSchema>;

// --- Claims -------------------------------------------------------------------------

export const INSURER_CLAIM_VIEWS = [
  'to_send',
  'with_insurer',
  'rejected',
  'settled',
  'all',
] as const;
export type InsurerClaimListView = (typeof INSURER_CLAIM_VIEWS)[number];

export const insurerClaimListQuerySchema = z.object({
  view: z.enum(INSURER_CLAIM_VIEWS).default('to_send'),
  payerId: z.uuid().optional(),
});

export const insurerClaimSummarySchema = z.object({
  id: z.uuid(),
  claimNumber: z.string(),
  state: z.enum(INSURER_CLAIM_STATES),
  sentence: z.string(),
  payer: z.object({ id: z.uuid(), name: z.string() }),
  schemeName: z.string(),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  visit: z.object({ id: z.uuid(), visitNumber: z.string(), startedAt: z.iso.datetime() }),
  claimed: amountSchema,
  paid: amountSchema,
  outstanding: amountSchema,
  /** For a draft: what it would claim now. */
  estimated: amountSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  submittedAt: z.iso.datetime().nullable(),
  problemCount: z.number().int(),
  version: z.number().int(),
});
export type InsurerClaimSummary = z.infer<typeof insurerClaimSummarySchema>;

export const insurerClaimListSchema = z.object({ items: z.array(insurerClaimSummarySchema) });

export const insurerClaimLineSchema = z.object({
  chargeId: z.uuid(),
  itemCode: z.string(),
  itemName: z.string(),
  quantity: z.number().int(),
  unitPrice: amountSchema,
  total: amountSchema,
  payer: amountSchema,
  patient: amountSchema,
  serviceDate: z.string(),
  preauthReference: z.string().nullable(),
});

export const insurerClaimDetailSchema = insurerClaimSummarySchema.extend({
  cover: coverageViewSchema,
  writtenOff: amountSchema,
  rejectionReason: z.string().nullable(),
  problems: z.array(
    z.object({
      code: z.enum(INSURER_READINESS_CODES),
      subjectId: z.uuid().nullable(),
      sentence: z.string(),
    }),
  ),
  lines: z.array(insurerClaimLineSchema),
  diagnoses: z.array(
    z.object({ code: z.string().nullable(), display: z.string(), rank: z.string() }),
  ),
  preauths: z.array(insurerPreauthViewSchema),
  events: z.array(
    z.object({
      kind: z.string(),
      sentence: z.string(),
      amount: amountSchema.nullable(),
      note: z.string().nullable(),
      occurredAt: z.iso.datetime(),
      actor: actorSchema,
    }),
  ),
  writeOffs: z.array(
    z.object({
      id: z.uuid(),
      amount: amountSchema,
      reasonCode: z.enum(INSURER_WRITE_OFF_REASONS),
      reasonNote: z.string().nullable(),
      status: z.enum(['requested', 'approved', 'rejected', 'withdrawn']),
      requestedBy: actorSchema,
      requestedAt: z.iso.datetime(),
      decidedBy: actorSchema.nullable(),
      decisionNote: z.string().nullable(),
    }),
  ),
  facility: z.object({ name: z.string(), mflCode: z.string().nullable() }),
  canSubmit: z.boolean(),
});
export type InsurerClaimDetail = z.infer<typeof insurerClaimDetailSchema>;

export const recordRejectionRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: z.string().trim().min(2).max(500),
});

export const claimVersionRequestSchema = z.object({ expectedVersion: z.number().int().min(1) });

export const insurerWriteOffRequestSchema = z
  .object({
    amount: amountSchema,
    reasonCode: z.enum(INSURER_WRITE_OFF_REASONS),
    reasonNote: z.string().trim().min(2).max(500).optional(),
  })
  .refine((v) => v.reasonCode !== 'other' || v.reasonNote !== undefined, {
    message: 'Say why, for Other',
    path: ['reasonNote'],
  });

export const insurerWriteOffDecisionSchema = z.object({
  note: z.string().trim().max(500).optional(),
});

// --- Invoices -----------------------------------------------------------------------

export const issueInsurerInvoiceRequestSchema = z.object({
  payerId: z.uuid(),
  claims: z
    .array(z.object({ id: z.uuid(), expectedVersion: z.number().int().min(1) }))
    .min(1)
    .max(200),
});

export const insurerInvoiceSummarySchema = z.object({
  id: z.uuid(),
  invoiceNumber: z.string(),
  payer: z.object({ id: z.uuid(), name: z.string() }),
  issuedAt: z.iso.datetime(),
  total: amountSchema,
  claimCount: z.number().int(),
  outstanding: amountSchema,
});
export const insurerInvoiceListSchema = z.object({ items: z.array(insurerInvoiceSummarySchema) });

export const insurerInvoiceDetailSchema = insurerInvoiceSummarySchema.extend({
  issuedBy: actorSchema,
  facility: z.object({ name: z.string(), mflCode: z.string().nullable() }),
  /**
   * Every claim on the invoice, so its total adds up. A claim whose visit carries a
   * sensitivity label the reader isn't granted shows no patient or member number (null).
   */
  claims: z.array(
    insurerClaimSummarySchema.extend({
      patient: insurerClaimSummarySchema.shape.patient.nullable(),
      memberNumber: z.string().nullable(),
    }),
  ),
});
export type InsurerInvoiceDetail = z.infer<typeof insurerInvoiceDetailSchema>;

// --- Remittances --------------------------------------------------------------------

const allocationSchema = z.object({ claimId: z.uuid(), amount: amountSchema });

export const recordRemittanceRequestSchema = z.object({
  payerId: z.uuid(),
  reference: z.string().trim().min(1).max(64),
  method: z.enum(REMITTANCE_METHODS),
  receivedOn: isoDaySchema,
  amount: amountSchema,
  note: z.string().trim().max(500).optional(),
  allocations: z.array(allocationSchema).max(500),
});

export const allocateRemittanceRequestSchema = z.object({
  allocations: z.array(allocationSchema).min(1).max(500),
});

export const remittanceSummarySchema = z.object({
  id: z.uuid(),
  remittanceNumber: z.string(),
  reference: z.string(),
  payer: z.object({ id: z.uuid(), name: z.string() }),
  method: z.enum(REMITTANCE_METHODS),
  receivedOn: z.string(),
  amount: amountSchema,
  allocated: amountSchema,
  unapplied: amountSchema,
});
export const remittanceListSchema = z.object({ items: z.array(remittanceSummarySchema) });

export const remittanceDetailSchema = remittanceSummarySchema.extend({
  note: z.string().nullable(),
  createdBy: actorSchema,
  allocations: z.array(
    z.object({
      claimId: z.uuid(),
      claimNumber: z.string(),
      amount: amountSchema,
      allocatedAt: z.iso.datetime(),
    }),
  ),
});
export type RemittanceDetail = z.infer<typeof remittanceDetailSchema>;

/** Open claims of one insurer, oldest invoiced first, for allocating a payment. */
export const openInsurerClaimsQuerySchema = z.object({ payerId: z.uuid() });
export const openInsurerClaimListSchema = z.object({
  items: z.array(
    z.object({
      id: z.uuid(),
      claimNumber: z.string(),
      invoiceNumber: z.string().nullable(),
      invoicedAt: z.iso.datetime(),
      /** Null when the claim's visit carries a label the reader isn't granted. */
      patientNumber: z.string().nullable(),
      outstanding: amountSchema,
    }),
  ),
});

// --- Debtors ------------------------------------------------------------------------

export const debtorRowSchema = z.object({
  payer: z.object({ id: z.uuid(), name: z.string() }),
  bands: z.record(z.enum(DEBTOR_AGE_BANDS), amountSchema),
  total: amountSchema,
  claimCount: z.number().int(),
  unapplied: amountSchema,
  notInvoiced: amountSchema,
  notInvoicedCount: z.number().int(),
});
export const debtorListSchema = z.object({
  asOf: z.string(),
  items: z.array(debtorRowSchema),
  totals: debtorRowSchema.omit({ payer: true }),
});
export type DebtorList = z.infer<typeof debtorListSchema>;
